import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { AuthProvider as UiAuthProvider, useAuthContext } from '@stenpro/ui-components';
import { AuthContext, type AuthUser } from './auth-context';
import { combineRoleRequirement, type UserRole } from '@/types/user';
import {
  createAuthConfig,
  DEFAULT_ACTOR,
  DEFAULT_ORGANIZATION,
  DEFAULT_TENANT,
} from '@/authConfig';
import { registerAuthClient } from '@/authClient';

function resolveAppRole(roles: string[] | undefined): UserRole {
  if (!roles || roles.length === 0) {
    return 'User';
  }

  const mapped = roles
    .map((role): UserRole | undefined => {
      if (!role) {
        return undefined;
      }

      // Prefer explicit app roles when present.
      if (role === 'Administrator') {
        return 'Administrator';
      }

      if (role === 'ProjectManager') {
        return 'ProjectManager';
      }

      // Demo/dev tokens often contain scope-like roles; map them to app roles.
      if (
        role === 'project.owner' ||
        role === 'PortalAdmin' ||
        role === 'Project Developer' ||
        role === 'ProjectDeveloper' ||
        role === 'project.admin'
      ) {
        return 'Administrator';
      }

      return undefined;
    })
    .filter((value): value is UserRole => Boolean(value));

  return combineRoleRequirement(...mapped) ?? 'User';
}

function AuthBridge({ children }: PropsWithChildren) {
  const { client, config, state } = useAuthContext();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const authMode = config.mode ?? 'stub';
    registerAuthClient(client);
    const normalize = (value?: string | null) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed) return undefined;
      const lower = trimmed.toLowerCase();
      if (lower === 'undefined' || lower === 'null') return undefined;
      return trimmed;
    };
    const tenantId =
      normalize(state.tenantId) ??
      normalize(state.user?.tenantId) ??
      DEFAULT_TENANT;
    const organizationId =
      normalize(state.user?.organizationId) ?? DEFAULT_ORGANIZATION;

    (async () => {
      try {
        if (authMode === 'oidc') {
          await client.ensureValidAccessToken({ tenantId, organizationId });
        } else if (import.meta.env.DEV) {
          await client.refreshTokens({ tenantId, organizationId });
        } else {
          await client.ensureValidAccessToken({ tenantId, organizationId });
        }
      } catch {
        // Token provisioning failure should not block the UI.
      } finally {
        if (!cancelled) {
          setIsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, config.mode, state.tenantId, state.user?.tenantId, state.user?.organizationId]);

  const user = useMemo<AuthUser | null>(() => {
    if (!state.isAuthenticated) {
      return null;
    }

    const resolvedRole = resolveAppRole(state.user?.roles);
    return {
      displayName: state.user?.name ?? state.user?.email ?? DEFAULT_ACTOR,
      userId: state.user?.id ?? DEFAULT_ACTOR,
      role: resolvedRole,
      roles: state.user?.roles ?? [],
      canRegisterTimeForOthers: resolvedRole !== 'User',
      leaveOverviewMinimumRole: resolvedRole,
      leaveOverviewAllowCompanyScope: true,
    };
  }, [state.isAuthenticated, state.user]);

  if (!isReady) {
    return <AuthContext.Provider value={user} />;
  }

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}

export const AuthProvider = ({ children }: PropsWithChildren) => (
  <UiAuthProvider config={createAuthConfig()}>
    <AuthBridge>{children}</AuthBridge>
  </UiAuthProvider>
);

export default AuthProvider;
